import React from 'react';
import { compose } from 'redux';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { ensureCurrentUser } from '../../util/data';
import { getImprovedLocationText, trimText } from '../../util/misc';
import {
  SecondaryButton,
  NamedLink,
  VerificationBadges,
  Bubble,
  FavoriteListingButton,
  ShareListingButton,
} from '../../components';

import css from './ListingCard.module.css';
import { getMembershipId } from '../../util/dataExtractors';

import { ReactComponent as CrownIcon } from './crownIcon.svg'

const MIN_LENGTH_FOR_LONG_WORDS = 10;

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    currentUser,
    searchString,
    viewport,
    onListingClick,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', description = null, price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const isMobile = viewport.width <= 768;
  const trimmedTitle = isMobile ? trimText(title, 33) : title;
  const charsCount = 24;

  const membershipId = getMembershipId(author);
  const isVerifiedUser = author?.attributes?.profile?.publicData?.isVerified

  const {
    listingType,
    categoryLevel1,
    buyerProfile,
    sellingPrincipalProfile,
    Location_preference,
    approximate_asset_location,
    currency,
    asking_price,
    Budget_minimum,
    Budget_Maximum,
  } = publicData || {};

  const categories = config?.categoryConfiguration?.categories || [];
  const categoryLabel = categories.find(c => c.id == categoryLevel1)?.name;

  const isSale = listingType === 'sell';
  const isSearch = listingType === 'search';

  const locationText = getImprovedLocationText(publicData);
  const locationTextMaybe = locationText || "Unknown";

  const trimmedTextMaybe = isSearch
    ? trimText(locationTextMaybe, charsCount)
    : trimText(locationTextMaybe, charsCount + 10);

  const locationSubject = isSearch
    ? "Location preference: "
    : "Location: ";

  const shouldShowCrownIcon = listingType === 'sell' && user?.attributes?.profile?.metadata?.isPremium !== true;

  const buyerProfileConfig = config?.listing?.listingFields.find(config => config.key === "buyerProfile");
  const sellerProfileConfig = config?.listing?.listingFields.find(config => config.key === "sellingPrincipalProfile");

  let buyerProfileLabel = "Not specified";
  let sellerProfileLabel = "Not specified";

  if (buyerProfileConfig) {
    const selectedOption = buyerProfileConfig.enumOptions.find(option => option.option === buyerProfile);
    buyerProfileLabel = selectedOption ? selectedOption.label : "Not specified";
  }

  if (sellerProfileConfig) {
    const selectedOption = sellerProfileConfig.enumOptions.find(option => option.option === sellingPrincipalProfile);
    sellerProfileLabel = selectedOption ? selectedOption.label : "Not specified";
  }

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      to={{ state: { searchString } }}
      onClick={() => {
        if (onListingClick) {
          onListingClick(listing);
        }
      }}
    >
      <div className={css.content}>
        <div className={css.item}>
          <div className={css.header}>
            <Bubble type={listingType} />
            {/* {shouldShowCrownIcon &&<CrownIcon/>} */}
            <div className={css.infoBubble}>
              {categoryLabel}
            </div>

            {/* <NamedLink className={css.debtLink} name="DebtPage">
              <span>Debt</span>
              <span className={css.desktopOnly}>Advisory</span>
            </NamedLink> */}

            {/* Stops its own click, so saving does not also open the listing. */}
            <FavoriteListingButton listingId={id} />
          </div>
          <div className={css.title}>
            {richText(trimmedTitle, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {isSearch ? <div className={css.price}>
            Budget: {Budget_minimum?.toLocaleString()} - {Budget_Maximum?.toLocaleString()} {currency}
          </div> : <div className={css.price}>
            Asking price: {asking_price ? `${asking_price.toLocaleString()} ${currency}` : "Undisclosed"}
          </div>}
          <div className={css.locationPlace}>
            {locationSubject}
            <span className={css.footerInfo}>
              {/* {!!currentUser || isSearch ? trimmedTextMaybe : "MEMBERS ONLY"} */}
              {trimmedTextMaybe}
            </span>
          </div>
        </div>
      </div>
      <div className={css.footerWrapper}>
        <div className={css.footer}>
          <div className={css.footerItem}>
            {isSearch ? "Buyer" : "Seller"}: <span className={css.footerInfo}>
              {isSearch ? buyerProfileLabel : sellerProfileLabel}
            </span>
          </div>
          <div className={css.desktopOnly}>
            <svg width="4" height="4" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
            </svg>
          </div>
          <div className={css.footerItem}>
            <div className={css.verificationBadges}>
              <VerificationBadges user={author} />
            </div>
          </div>
        </div>
        <div className={css.footerActions}>
          {/* Stops its own click, so sharing does not also open the listing. */}
          <ShareListingButton listing={currentListing} />
          <SecondaryButton type="button" className={css.viewButton}>
            <span>View</span>
            <span className={css.desktopOnly}>more</span>
          </SecondaryButton>
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  onListingClick: null,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
  onListingClick: func,
};

const ListingCard = compose(
  injectIntl,
  withViewport,
)(ListingCardComponent);

export default ListingCard;
